const DEFAULT_GRADIENT_STOPS = [
  { id: "stop-1", color: "#2A7B9B", position: 0 },
  { id: "stop-2", color: "#57C785", position: 50 },
  { id: "stop-3", color: "#EDDD53", position: 100 },
];

export const createDefaultGradientState = () => ({
  type: "linear",
  angle: 90,
  radialShape: "circle",
  radialPosition: "center",
  stops: DEFAULT_GRADIENT_STOPS.map((stop) => ({ ...stop })),
});

const RANDOM_LINEAR_ANGLES = [0, 45, 90, 135, 160, 180, 225, 270, 315];
const RANDOM_RADIAL_POSITIONS = [
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top left",
  "top right",
  "bottom left",
  "bottom right",
];

const RANDOM_GRADIENT_MOODS = [
  { id: "pastel", saturation: [42, 62], lightness: [78, 92] },
  { id: "bright", saturation: [58, 78], lightness: [52, 72] },
  { id: "soft", saturation: [35, 55], lightness: [64, 82] },
  { id: "deep", saturation: [48, 68], lightness: [28, 48] },
];

const pickRandom = (items = []) => items[Math.floor(Math.random() * items.length)];
const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const hslToHex = (hue, saturation, lightness) => {
  const normalizedHue = ((hue % 360) + 360) % 360;
  const s = Math.max(0, Math.min(100, saturation)) / 100;
  const l = Math.max(0, Math.min(100, lightness)) / 100;
  const chroma = (1 - Math.abs((2 * l) - 1)) * s;
  const huePrime = normalizedHue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;

  if (huePrime >= 0 && huePrime < 1) {
    r = chroma;
    g = x;
  } else if (huePrime < 2) {
    r = x;
    g = chroma;
  } else if (huePrime < 3) {
    g = chroma;
    b = x;
  } else if (huePrime < 4) {
    g = x;
    b = chroma;
  } else if (huePrime < 5) {
    r = x;
    b = chroma;
  } else {
    r = chroma;
    b = x;
  }

  const match = l - (chroma / 2);
  return `#${normalizeHexChannel((r + match) * 255)}${normalizeHexChannel((g + match) * 255)}${normalizeHexChannel((b + match) * 255)}`.toUpperCase();
};

const createRandomStopId = (index) =>
  `stop-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`;

const buildHarmoniousHues = (baseHue) => {
  const style = pickRandom(["analogous", "split", "monochrome"]);
  if (style === "monochrome") {
    return [baseHue, baseHue, baseHue];
  }
  if (style === "split") {
    return [baseHue, (baseHue + 150) % 360, (baseHue + 210) % 360];
  }
  return [baseHue, (baseHue + randomInt(18, 36)) % 360, (baseHue + randomInt(42, 72)) % 360];
};

const buildRandomGradientStops = (mood) => {
  const hues = buildHarmoniousHues(randomInt(0, 359));
  const stopCount = pickRandom([2, 3, 3, 4]);
  const positions = stopCount === 2
    ? [0, 100]
    : stopCount === 3
      ? [0, pickRandom([38, 42, 45, 50, 55]), 100]
      : [0, randomInt(28, 38), randomInt(62, 72), 100];

  return positions.map((position, index) => {
    const hue = hues[index % hues.length];
    const saturation = randomInt(mood.saturation[0], mood.saturation[1]);
    const lightnessSpread = mood.lightness[1] - mood.lightness[0];
    const lightness = mood.lightness[0] + Math.round((lightnessSpread * index) / Math.max(1, stopCount - 1));
    return {
      id: createRandomStopId(index),
      color: hslToHex(hue, saturation, lightness),
      position,
    };
  });
};

/** Classroom-friendly gradient with harmonious hues and readable contrast. */
export const createRandomGradientState = () => {
  const mood = pickRandom(RANDOM_GRADIENT_MOODS);
  const type = Math.random() < 0.72 ? "linear" : "radial";

  return {
    type,
    angle: pickRandom(RANDOM_LINEAR_ANGLES),
    radialShape: Math.random() < 0.85 ? "circle" : "ellipse",
    radialPosition: pickRandom(RANDOM_RADIAL_POSITIONS),
    stops: buildRandomGradientStops(mood),
  };
};

export const isGradientBackground = (value) =>
  typeof value === "string" && /(?:linear|radial|conic)-gradient\s*\(/i.test(value.trim());

const clampPosition = (value) => Math.max(0, Math.min(100, Math.round(Number(value) || 0)));

const normalizeHexChannel = (channel) => {
  const parsed = Math.max(0, Math.min(255, Math.round(channel)));
  return parsed.toString(16).padStart(2, "0");
};

export const hexToRgbChannels = (hexColor) => {
  const normalized = hexColor.replace("#", "");
  const expanded = normalized.length === 3
    ? normalized.split("").map((character) => `${character}${character}`).join("")
    : normalized;

  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  };
};

export const interpolateHexColor = (startColor, endColor, ratio) => {
  const start = hexToRgbChannels(startColor.startsWith("#") ? startColor : "#000000");
  const end = hexToRgbChannels(endColor.startsWith("#") ? endColor : "#000000");
  const mix = (left, right) => left + ((right - left) * ratio);

  return `#${normalizeHexChannel(mix(start.r, end.r))}${normalizeHexChannel(mix(start.g, end.g))}${normalizeHexChannel(mix(start.b, end.b))}`.toUpperCase();
};

const sortStops = (stops = []) =>
  [...stops].sort((left, right) => left.position - right.position);

const formatStopsForCss = (stops = []) =>
  sortStops(stops)
    .map((stop) => `${stop.color} ${clampPosition(stop.position)}%`)
    .join(", ");

export const buildGradientCss = (state = createDefaultGradientState()) => {
  const stopsCss = formatStopsForCss(state.stops);
  if (!stopsCss) {
    return "";
  }

  if (state.type === "radial") {
    const shape = state.radialShape === "ellipse" ? "ellipse" : "circle";
    const position = state.radialPosition || "center";
    return `radial-gradient(${shape} at ${position}, ${stopsCss})`;
  }

  const angle = Number.isFinite(Number(state.angle)) ? Number(state.angle) : 90;
  return `linear-gradient(${angle}deg, ${stopsCss})`;
};

const splitGradientStops = (stopsPart) => {
  const stops = [];
  let current = "";
  let depth = 0;

  for (let index = 0; index < stopsPart.length; index += 1) {
    const character = stopsPart[index];
    if (character === "(") {
      depth += 1;
      current += character;
      continue;
    }
    if (character === ")") {
      depth -= 1;
      current += character;
      continue;
    }
    if (character === "," && depth === 0) {
      if (current.trim()) {
        stops.push(current.trim());
      }
      current = "";
      continue;
    }
    current += character;
  }

  if (current.trim()) {
    stops.push(current.trim());
  }

  return stops;
};

const parseStopToken = (token, index) => {
  const match = token.match(/^(.*?)\s+(\d+(?:\.\d+)?)%\s*$/);
  if (!match) {
    return null;
  }

  const color = match[1].trim();
  return {
    id: `stop-${index + 1}`,
    color,
    position: clampPosition(match[2]),
  };
};

export const parseGradientBackground = (value) => {
  if (!isGradientBackground(value)) {
    return null;
  }

  const trimmed = value.trim();
  const linearMatch = trimmed.match(/^linear-gradient\(\s*([^,]+)\s*,([\s\S]+)\)$/i);
  if (linearMatch) {
    const config = linearMatch[1].trim();
    const angleMatch = config.match(/^(-?\d+(?:\.\d+)?)deg$/i);
    const stops = splitGradientStops(linearMatch[2])
      .map(parseStopToken)
      .filter(Boolean);

    if (stops.length >= 2) {
      return {
        type: "linear",
        angle: angleMatch ? Number(angleMatch[1]) : 90,
        radialShape: "circle",
        radialPosition: "center",
        stops,
      };
    }
  }

  const radialMatch = trimmed.match(/^radial-gradient\(\s*([^,]+)\s*,([\s\S]+)\)$/i);
  if (radialMatch) {
    const config = radialMatch[1].trim();
    const shapeMatch = config.match(/^(circle|ellipse)(?:\s+at\s+(.+))?$/i);
    const stops = splitGradientStops(radialMatch[2])
      .map(parseStopToken)
      .filter(Boolean);

    if (stops.length >= 2) {
      return {
        type: "radial",
        angle: 90,
        radialShape: shapeMatch?.[1]?.toLowerCase() === "ellipse" ? "ellipse" : "circle",
        radialPosition: shapeMatch?.[2]?.trim() || "center",
        stops,
      };
    }
  }

  return null;
};

export const getGradientStateFromValue = (value) =>
  parseGradientBackground(value) || createDefaultGradientState();

export const getColorAtGradientPosition = (stops, position) => {
  const sorted = sortStops(stops);
  if (sorted.length === 0) {
    return "#ffffff";
  }
  if (sorted.length === 1) {
    return sorted[0].color;
  }

  const clamped = clampPosition(position);
  if (clamped <= sorted[0].position) {
    return sorted[0].color;
  }
  if (clamped >= sorted[sorted.length - 1].position) {
    return sorted[sorted.length - 1].color;
  }

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const left = sorted[index];
    const right = sorted[index + 1];
    if (clamped >= left.position && clamped <= right.position) {
      const range = right.position - left.position || 1;
      const ratio = (clamped - left.position) / range;
      if (left.color.startsWith("#") && right.color.startsWith("#")) {
        return interpolateHexColor(left.color, right.color, ratio);
      }
      return left.color;
    }
  }

  return sorted[0].color;
};

export const createGradientStop = (stops, position) => ({
  id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  color: getColorAtGradientPosition(stops, position),
  position: clampPosition(position),
});
